// src/components/admin/UserEngagementModal.tsx
// Modal de perfil completo de engajamento do usuário

import React, { useEffect, useState } from 'react';
import { X, RefreshCw, Clock, Zap, Calendar, BookOpen, Target, TrendingUp, User } from 'lucide-react';
import './UserEngagementModal.css';

interface ModalDetails {
    username: string;
    lastLoginEvent: string | null;
    lastStudyEvent: string | null;
    timeTotal: number;
    timeLast7Days: number;
    activeDaysLast30: number;
    currentStreak: number;
    topSubdecks: { name: string; eventsCount: number }[];
    topApostilas: { name: string; eventsCount: number }[];
    studyDistribution: { flashcards: number; questions: number; apostilas: number };
    cardsStudied: number;
    cardsMastered: number;
    questionsAnswered: number;
    activityLast14Days: { date: string; events: number }[];
    timelineLast14Days: { date: string; type: 'study' | 'login'; count: number }[];
    globalStats?: { currentStreak?: number; totalCardsStudied?: number };
}

interface UserEngagementModalProps {
    userId: string;
    onClose: () => void;
    onResetStats: (userId: string, username: string) => void;
}

function formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}min`;
    return `${minutes}min`;
}

function formatRelativeTime(isoTimestamp: string | null): string {
    if (!isoTimestamp) return 'Nunca';
    const diffMs = Date.now() - new Date(isoTimestamp).getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 1) return 'Há menos de 1h';
    if (diffHours < 24) return `Há ${diffHours}h`;
    if (diffDays === 1) return 'Ontem';
    if (diffDays < 30) return `Há ${diffDays} dias`;
    return new Date(isoTimestamp).toLocaleDateString('pt-BR');
}

function computeInsight(details: ModalDetails): { label: string; color: string } {
    const { currentStreak, lastStudyEvent, activityLast14Days, studyDistribution, cardsStudied } = details;

    if (!lastStudyEvent && cardsStudied === 0) {
        return { label: 'Ainda não começou', color: '#6B7280' };
    }

    if (currentStreak >= 14) {
        return { label: 'Estudante consistente de longo prazo', color: '#16A34A' };
    }

    // Crescimento: últimos 7 dias vs 7 dias anteriores
    const recent7 = activityLast14Days.slice(7).reduce((s, d) => s + d.events, 0);
    const prev7 = activityLast14Days.slice(0, 7).reduce((s, d) => s + d.events, 0);
    if (recent7 > prev7 * 1.2 && recent7 > 0) {
        return { label: 'Acelerando o ritmo', color: '#2563EB' };
    }

    if (lastStudyEvent) {
        const diffDays = Math.floor((Date.now() - new Date(lastStudyEvent).getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays > 14 && cardsStudied > 10) {
            return { label: 'Em risco de abandono', color: '#DC2626' };
        }
        if (diffDays <= 3 && currentStreak < 7) {
            return { label: 'Retomando os estudos', color: '#D97706' };
        }
    }

    const maxPct = Math.max(studyDistribution.flashcards, studyDistribution.questions, studyDistribution.apostilas);
    if (maxPct >= 70) {
        const tipo = studyDistribution.flashcards >= 70 ? 'Flashcards'
            : studyDistribution.questions >= 70 ? 'Questões'
                : 'Apostilas';
        return { label: `Focado em ${tipo}`, color: '#7C3AED' };
    }

    return { label: 'Estudante ativo', color: '#003BD1' };
}

// Mini bar chart para atividade 14 dias
function ActivityBars({ data }: { data: { date: string; events: number }[] }) {
    const max = Math.max(...data.map(d => d.events), 1);
    return (
        <div className="uem-bar-chart">
            {data.map((d, i) => {
                const pct = (d.events / max) * 100;
                const label = new Date(d.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
                return (
                    <div key={i} className="uem-bar-col">
                        <div className="uem-bar-track">
                            <div
                                className="uem-bar-fill"
                                style={{ height: d.events > 0 ? `${pct}%` : '2px' }}
                                title={`${label}: ${d.events} cards`}
                            />
                        </div>
                        {i % 2 === 0 && <span className="uem-bar-label">{label}</span>}
                    </div>
                );
            })}
        </div>
    );
}

// Pizza chart SVG puro
function PieChart({ data }: { data: { label: string; value: number; color: string }[] }) {
    const total = data.reduce((s, d) => s + d.value, 0) || 1;
    let cumAngle = 0;

    const slices = data.map(d => {
        const angle = (d.value / total) * 360;
        const start = cumAngle;
        cumAngle += angle;
        return { ...d, startAngle: start, angle };
    });

    function polarToXY(cx: number, cy: number, r: number, angleDeg: number) {
        const rad = ((angleDeg - 90) * Math.PI) / 180;
        return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
    }

    function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
        const start = polarToXY(cx, cy, r, startAngle);
        const end = polarToXY(cx, cy, r, endAngle);
        const largeArc = endAngle - startAngle > 180 ? 1 : 0;
        return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
    }

    return (
        <div className="uem-pie-wrapper">
            <svg viewBox="0 0 120 120" className="uem-pie-svg">
                {slices.map((s, i) => (
                    s.angle > 0 ? (
                        <path
                            key={i}
                            d={describeArc(60, 60, 50, s.startAngle, s.startAngle + s.angle)}
                            fill={s.color}
                            stroke="#fff"
                            strokeWidth="1.5"
                        />
                    ) : null
                ))}
            </svg>
            <div className="uem-pie-legend">
                {data.map((d, i) => (
                    <div key={i} className="uem-pie-legend-item">
                        <span className="uem-pie-dot" style={{ backgroundColor: d.color }} />
                        <span className="uem-pie-label">{d.label}</span>
                        <span className="uem-pie-value">{d.value}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

const UserEngagementModal: React.FC<UserEngagementModalProps> = ({ userId, onClose, onResetStats }) => {
    const [details, setDetails] = useState<ModalDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const token = localStorage.getItem('accessToken');
                const res = await fetch(`/api/admin/engagement/users/${userId}/details`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!res.ok) throw new Error();
                const data = await res.json();
                setDetails(data);
            } catch {
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [userId]);

    // Fechar com Escape
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const statusBadge = (() => {
        if (!details?.lastStudyEvent) return { label: 'Inativo', cls: 'inactive' };
        const diffDays = Math.floor((Date.now() - new Date(details.lastStudyEvent).getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays <= 7) return { label: 'Ativo', cls: 'active' };
        if (diffDays <= 21) return { label: 'Risco', cls: 'risk' };
        return { label: 'Inativo', cls: 'inactive' };
    })();

    const insight = details ? computeInsight(details) : null;

    const pieData = details ? [
        { label: 'Flashcards', value: details.studyDistribution.flashcards, color: '#003BD1' },
        { label: 'Questões', value: details.studyDistribution.questions, color: '#2563EB' },
        { label: 'Apostilas', value: details.studyDistribution.apostilas, color: '#93C5FD' },
    ] : [];

    return (
        <div className="uem-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="uem-modal">
                {/* Header */}
                <div className="uem-modal-header">
                    <div className="uem-header-left">
                        <div className="uem-avatar">
                            {details?.username?.charAt(0).toUpperCase() ?? <User size={20} />}
                        </div>
                        <div className="uem-header-info">
                            <h2 className="uem-username">{details?.username ?? '...'}</h2>
                            {!loading && (
                                <span className={`uem-status-badge ${statusBadge.cls}`}>
                                    {statusBadge.label}
                                </span>
                            )}
                        </div>
                    </div>
                    <button className="uem-close-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="uem-modal-body">
                    {loading && (
                        <div className="uem-loading">
                            <div className="uem-spinner" />
                            <p>Carregando dados</p>
                        </div>
                    )}

                    {error && (
                        <div className="uem-error">
                            <p>Erro ao carregar dados do usuário</p>
                        </div>
                    )}

                    {details && (
                        <>
                            {/* Seção 1: Presença vs Aprendizado */}
                            <div className="uem-section">
                                <h3 className="uem-section-title">Presença vs Aprendizado</h3>
                                <div className="uem-presence-grid">
                                    <div className="uem-presence-card">
                                        <div className="uem-presence-icon login">
                                            <User size={18} />
                                        </div>
                                        <div>
                                            <div className="uem-presence-label">Último Login</div>
                                            <div className="uem-presence-value">{formatRelativeTime(details.lastLoginEvent)}</div>
                                        </div>
                                    </div>
                                    <div className="uem-presence-card">
                                        <div className="uem-presence-icon study">
                                            <BookOpen size={18} />
                                        </div>
                                        <div>
                                            <div className="uem-presence-label">Último Estudo</div>
                                            <div className="uem-presence-value">{formatRelativeTime(details.lastStudyEvent)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Seção 2: Métricas de Estudo */}
                            <div className="uem-section">
                                <h3 className="uem-section-title">Métricas de Estudo</h3>
                                <div className="uem-metrics-grid">
                                    <div className="uem-metric-card">
                                        <Clock size={18} className="uem-metric-icon" />
                                        <div className="uem-metric-value">{formatTime(details.timeTotal)}</div>
                                        <div className="uem-metric-label">Tempo total</div>
                                    </div>
                                    <div className="uem-metric-card">
                                        <TrendingUp size={18} className="uem-metric-icon" />
                                        <div className="uem-metric-value">{formatTime(details.timeLast7Days)}</div>
                                        <div className="uem-metric-label">Últimos 7 dias</div>
                                    </div>
                                    <div className="uem-metric-card">
                                        <Calendar size={18} className="uem-metric-icon" />
                                        <div className="uem-metric-value">{details.activeDaysLast30}</div>
                                        <div className="uem-metric-label">Dias ativos (30d)</div>
                                    </div>
                                    <div className="uem-metric-card">
                                        <Zap size={18} className="uem-metric-icon" />
                                        <div className="uem-metric-value">{details.currentStreak}</div>
                                        <div className="uem-metric-label">Streak atual</div>
                                    </div>
                                </div>
                            </div>

                            {/* Seção 3: Conteúdo Estudado */}
                            <div className="uem-section">
                                <h3 className="uem-section-title">Conteúdo Estudado</h3>

                                {/* Listas: Subdecks e Apostilas lado a lado */}
                                {(details.topSubdecks.length > 0 || details.topApostilas.length > 0) && (
                                    <div className="uem-top-lists">
                                        {details.topSubdecks.length > 0 && (
                                            <div className="uem-top-list-col">
                                                <div className="uem-content-subtitle">Top Subdecks</div>
                                                {details.topSubdecks.map((s, i) => (
                                                    <div key={i} className="uem-top-item">
                                                        <span className="uem-top-rank">#{i + 1}</span>
                                                        <span className="uem-top-name">{s.name}</span>
                                                        <span className="uem-top-count">{s.eventsCount}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {details.topApostilas.length > 0 && (
                                            <div className="uem-top-list-col">
                                                <div className="uem-content-subtitle">Top Apostilas</div>
                                                {details.topApostilas.map((a, i) => (
                                                    <div key={i} className="uem-top-item">
                                                        <span className="uem-top-rank">#{i + 1}</span>
                                                        <span className="uem-top-name">{a.name}</span>
                                                        <span className="uem-top-count">{a.eventsCount}x</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Pizza chart — largura total, abaixo das listas */}
                                <div className="uem-distribution-block">
                                    <div className="uem-content-subtitle">Distribuição por tipo</div>
                                    <PieChart data={pieData} />
                                </div>
                            </div>

                            {/* Seção 4: Progresso */}
                            <div className="uem-section">
                                <h3 className="uem-section-title">Progresso</h3>
                                <div className="uem-progress-row">
                                    <div className="uem-progress-item">
                                        <div className="uem-progress-value">{details.cardsStudied}</div>
                                        <div className="uem-progress-label">Cards estudados</div>
                                    </div>
                                    <div className="uem-progress-divider" />
                                    <div className="uem-progress-item">
                                        <div className="uem-progress-value">{details.cardsMastered}</div>
                                        <div className="uem-progress-label">Cards dominados</div>
                                    </div>
                                    <div className="uem-progress-divider" />
                                    <div className="uem-progress-item">
                                        <div className="uem-progress-value">{details.questionsAnswered}</div>
                                        <div className="uem-progress-label">Questões respondidas</div>
                                    </div>
                                    {details.cardsStudied > 0 && (
                                        <>
                                            <div className="uem-progress-divider" />
                                            <div className="uem-progress-item">
                                                <div className="uem-progress-value">
                                                    {Math.round((details.cardsMastered / details.cardsStudied) * 100)}%
                                                </div>
                                                <div className="uem-progress-label">Taxa de domínio</div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Seção 5: Gráfico de atividade */}
                            <div className="uem-section">
                                <h3 className="uem-section-title">Atividade — últimos 14 dias</h3>
                                <ActivityBars data={details.activityLast14Days} />
                            </div>

                            {/* Seção 6: Insight automático */}
                            {insight && (
                                <div className="uem-section">
                                    <h3 className="uem-section-title">Insight</h3>
                                    <div className="uem-insight-badge" style={{ borderColor: insight.color, color: insight.color }}>
                                        <Target size={16} />
                                        {insight.label}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                {details && (
                    <div className="uem-modal-footer">
                        <button
                            className="uem-reset-btn"
                            onClick={() => onResetStats(userId, details.username)}
                        >
                            <RefreshCw size={16} />
                            Resetar Estatísticas
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserEngagementModal;
